import { Box, Typography } from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import Star from '@material-ui/icons/Star';
import StarHalf from '@material-ui/icons/StarHalf';
import StarOutline from '@material-ui/icons/StarOutline';
import React from 'react';

// Presentation helpers that started life inside PageProductDetail.tsx and
// are imported by unrelated surfaces (product cards, the cart, the image
// overlay). They live here so the product page can be rewritten — and so
// PageProductOverlayImageCarousel can keep importing determineMediaType
// without closing an import cycle back through the page that renders it.

const useStyles = makeStyles((theme: Theme) =>
createStyles({
  starsElementContainer: {
    display: 'flex',
    marginBottom: '6px',
  },
  starFilled: {
    color: '#FFA41C',
    marginLeft: '-3px',
    marginRight: '3px',
    [theme.breakpoints.down('sm')]: {
      width: '24px',
      marginLeft: '-3px',
      marginRight: '3px',
    },
  },
  starEmpty: {
    color: '#d8d8d8',
    marginLeft: '-3px',
    marginRight: '3px',
    [theme.breakpoints.down('sm')]: {
      width: '24px',
    },
  },
  starsDescriptionText: {
    fontWeight: 'normal',
    fontSize: '13px',
    [theme.breakpoints.down('sm')]: {
      fontSize: '13px',
    },
    color: '#343434',
    fontFamily: 'Open sans, sans-serif',
    marginLeft: '10px',
    marginTop: '4px',
  },
  reviewItem: {
    margin: '0px 0px 16px 0px',
    padding: '8px 2px 24px 2px',
    width: 'auto',
    borderBottom: "1px solid #dedede",

    display: 'flex',
    flexDirection: 'row',

    [theme.breakpoints.down('sm')]: {
      margin: '0px 0px 22px 0px',
      padding: '6px 4px 28px 4px',
    },
  },
  reviewDisplayPicture: {
    borderRadius: '50%',
    width: '30px',
    height: '30px',
    padding: '10px',
    margin: '8px 18px 0px 6px',

    backgroundColor: '#d5d5d5',
    textAlign: 'center',

    [theme.breakpoints.down('sm')]: {
      display: 'none',
    },
  },
  reviewDisplayText: {
    fontFamily: 'Inter',
    fontWeight: 400,
    fontSize: '20px',
    color: '#fff',
    marginTop: '0px',
    width: '30px' // Set to equal to display picture size
  },
  reviewItemBoxDescription: {

  },
  reviewItemBox1: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    marginBottom: "5px",
    [theme.breakpoints.down('sm')]: {
      marginBottom: "6px",
    },
  },
  reviewItemBox2: {
    display: "flex",
    flexDirection: "row",
    alignItems: "flex-start",
    marginBottom: "5px",
    [theme.breakpoints.down('sm')]: {
      marginBottom: "6px",
    },
    // [theme.breakpoints.down('sm')]: {
    //   flexDirection: 'column',
    // },
  },
  reviewItemName: {
    fontFamily: 'Inter',
    fontWeight: 600,
    fontSize: '15px',
    color: "#121212",
    [theme.breakpoints.down('sm')]: {
      fontSize: '15px',
    },
  },
  reviewItemIsVerifiedText: {
    fontFamily: 'Inter',
    fontWeight: 400,
    fontSize: '11px',
    color: '#7A7A7A',
    paddingLeft: '1px',
    marginLeft: '10px', 
    [theme.breakpoints.down('sm')]: {
      fontSize: '11px',
    },
  },
  reviewItemDateAndPlaceText: {
    fontFamily: 'Inter',
    fontWeight: 400,
    fontSize: '11px',
    color: "#6c6c6c",
    marginBottom: '12px', 
    [theme.breakpoints.down('sm')]: {
      fontSize: '10px',
    },
  },
  reviewItemComment: {
    fontFamily: 'Inter',
    fontWeight: 300,
    fontSize: '15px',
    color: "#121212",
    [theme.breakpoints.down('sm')]: {
      fontSize: '14px',
    },
  },
}),
);

export function determineMediaType(filename: string): string {
  const videoExtensions = ['.webm', '.mp4'];
  const imageExtensions = ['.png', '.jpg', '.jpeg', '.gif', '.bmp', '.tiff', '.svg'];

  const lowerCaseFilename = filename.toLowerCase();

  for (const extension of videoExtensions) {
    if (lowerCaseFilename.endsWith(extension)) {
      return 'video';
    }
  }

  for (const extension of imageExtensions) {
    if (lowerCaseFilename.endsWith(extension)) {
      return 'image';
    }
  }

  return 'unknown';
}

interface StarsElementProps {
  stars: number
  withText?: boolean
  // colorStarFilled: string
  // colorStarEmpty: string
}

// Video Dari Page Component Element Carousel
export function StarsElementRate({stars, withText,}: StarsElementProps) {
  const classes = useStyles();

  return (
    <Box className={classes.starsElementContainer}>
      {
        stars >= 1 ? 
        <Star className={classes.starFilled}/> : 
        stars >= 0.5 ?
        <StarHalf className={classes.starFilled}/> :
        <StarOutline className={classes.starEmpty}/>
      }
      {
        stars >= 2 ? 
        <Star className={classes.starFilled}/> : 
        stars >= 1.5 ?
        <StarHalf className={classes.starFilled}/> :
        <StarOutline className={classes.starEmpty}/>
      }
      {
        stars >= 3 ? 
        <Star className={classes.starFilled}/> : 
        stars >= 2.5 ?
        <StarHalf className={classes.starFilled}/> :
        <StarOutline className={classes.starEmpty}/>
      }
      {
        stars >= 4 ? 
        <Star className={classes.starFilled}/> : 
        stars >= 3.5 ?
        <StarHalf className={classes.starFilled}/> :
        <StarOutline className={classes.starEmpty}/>
      }
      {
        stars >= 5 ? 
        <Star className={classes.starFilled}/> : 
        stars >= 4.5 ?
        <StarHalf className={classes.starFilled}/> :
        <StarOutline className={classes.starEmpty}/>
      }
      {
        withText === false ?
        null : 
        <Typography className={classes.starsDescriptionText}>
          {stars + "/5 stars"}
        </Typography>
      }
    </Box>
  )
}

export function StarsElement({stars, withText,}: StarsElementProps) {
  const classes = useStyles();

  return (
    <Box className={classes.starsElementContainer}>
      {
        stars >= 1 ? 
        <Star className={classes.starFilled}/> : 
        stars >= 0.5 ?
        <StarHalf className={classes.starFilled}/> :
        <StarOutline className={classes.starEmpty}/>
      }
      {
        stars >= 2 ? 
        <Star className={classes.starFilled}/> : 
        stars >= 1.5 ?
        <StarHalf className={classes.starFilled}/> :
        <StarOutline className={classes.starEmpty}/>
      }
      {
        stars >= 3 ? 
        <Star className={classes.starFilled}/> : 
        stars >= 2.5 ?
        <StarHalf className={classes.starFilled}/> :
        <StarOutline className={classes.starEmpty}/>
      }
      {
        stars >= 4 ? 
        <Star className={classes.starFilled}/> : 
        stars >= 3.5 ?
        <StarHalf className={classes.starFilled}/> :
        <StarOutline className={classes.starEmpty}/>
      }
      {
        stars >= 5 ? 
        <Star className={classes.starFilled}/> : 
        stars >= 4.5 ?
        <StarHalf className={classes.starFilled}/> :
        <StarOutline className={classes.starEmpty}/>
      }
      {
        withText === false ?
        null : 
        <Typography className={classes.starsDescriptionText}>
          {stars + " / 5 stars"}
        </Typography>
      }
    </Box>
  )
}

interface ReviewElementProps {
  name: string
  isVerified: boolean
  stars: number
  creationTimestamp: string
  headline: string
  message: string
}

export function ReviewElement({name, isVerified, stars, creationTimestamp, headline, message}: ReviewElementProps) {
  const classes = useStyles();

  // Create a new JavaScript Date object based on the timestamp
  // multiplied by 1000 so that the argument is in milliseconds, not seconds.
  var date = new Date(Number(creationTimestamp) * 1000);
  // Hours part from the timestamp
  var hours = date.getHours();
  // Minutes part from the timestamp
  var minutes = "0" + date.getMinutes();
  // Seconds part from the timestamp
  var seconds = "0" + date.getSeconds();

  // Will display time in 10:30:23 format
  var formattedTime = hours + ':' + minutes.substr(-2) + ':' + seconds.substr(-2);

  return (
    <Box className={classes.reviewItem}>
      <Box className={classes.reviewDisplayPicture}>
        <Typography className={classes.reviewDisplayText}>
          {name.substring(0,1)}
        </Typography>
      </Box>
      <Box className={classes.reviewItemBoxDescription}>
        <Box className={classes.reviewItemBox1}>
          <Typography className={classes.reviewItemName}>
            {name}
          </Typography>
          {
            isVerified ? 
            <Typography className={classes.reviewItemIsVerifiedText}>
              <li>Verified Purchase</li>
            </Typography> : 
            null
          }
        </Box>
        <Box className={classes.reviewItemBox2}>
          <StarsElement stars={stars} withText={false}/>
          {/* <Typography className={classes.reviewItemHeadline}>
            {headline}
          </Typography> */}
        </Box>
        <Typography className={classes.reviewItemDateAndPlaceText}>
          Reviewed on {date.toISOString().substring(0, 10)} {formattedTime}
        </Typography>
        <Typography className={classes.reviewItemComment}>
          {message}
        </Typography>
      </Box>
    </Box>
  )

}

export const countdownRendererNoText = ({ hours, minutes, seconds, completed }: any) => {

  if (completed) {
    return (
    <Box >
    </Box>);
  } else {
    return (
    <Box style={{
      width: '100%',
      display: 'flex',
      justifyContent: 'flex-start',
      alignItems: 'center',
      overflowX: 'clip',
    }}>
      <Box style={{
        borderRadius: '20px',
        color: '#ffffff',
        backgroundColor: '#121212',
        width: 'fit-content',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
      }}>
      <Typography style={{
        fontSize: '10px',
        fontFamily: 'OakesGrotesk',
        fontWeight: 400,
        padding: '4px 13px 1px 14px',
        backgroundColor: '#e10000',
        borderRadius: '20px',
        letterSpacing: '0.6px',
      }}>
        SALE
      </Typography>
      <Typography style={{
        fontSize: '10px',
        fontFamily: 'OakesGrotesk',
        fontWeight: 400,
        padding: '4px 14px 1px 10px',
      }}>
        {
          hours && Number(hours) > 0 ? 
          String(hours).padStart(2, '0') + ':' + String(minutes).padStart(2, '0') + ':' + String(seconds).padStart(2, '0') :
          String(minutes).padStart(2, '0') + ':' + String(seconds).padStart(2, '0')
        }
      </Typography>
      </Box>
    </Box>)
  }
};

export const countdownRenderer = ({ hours, minutes, seconds, completed }: any) => {

  if (completed) {
    return (
    <Box >
    </Box>);
  } else {
    return (
    <Box style={{
      width: '100%',
      display: 'flex',
      justifyContent: 'flex-start',
      alignItems: 'center',
      overflowX: 'clip',
    }}>
      <Box style={{
        borderRadius: '20px',
        color: '#ffffff',
        backgroundColor: '#121212',
        width: 'fit-content',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
      }}>
      <Typography style={{
        fontSize: '10px',
        fontFamily: 'OakesGrotesk',
        fontWeight: 400,
        padding: '4px 13px 1px 14px',
        backgroundColor: '#e10000',
        borderRadius: '20px',
        letterSpacing: '0.6px',
      }}>
        SALE
      </Typography>
      <Typography style={{
        fontSize: '10px',
        fontFamily: 'OakesGrotesk',
        fontWeight: 400,
        padding: '4px 14px 1px 10px',
      }}>
        {
          hours && Number(hours) > 0 ? 
          String(hours).padStart(2, '0') + ':' + String(minutes).padStart(2, '0') + ':' + String(seconds).padStart(2, '0') :
          String(minutes).padStart(2, '0') + ':' + String(seconds).padStart(2, '0')
        }
      </Typography>
      </Box>
      <Typography style={{
        fontSize: '10%',
        fontFamily: 'OakesGrotesk',
        fontWeight: 600,
        padding: '4px 0px 1px 8px',
        color: '#121212'
      }}>
        WEBSITE ONLY
      </Typography>
    </Box>)
  }
};

export const countdownRendererImgFullwidth = (imgSrc: string) => {
  return (({ hours, minutes, seconds, completed }: any) => {

  if (completed) {
    return (
    <Box >
    </Box>);
  } else {
    return (
    <Box style={{
      width: '100%',
      display: 'flex',
      justifyContent: 'flex-start',
      alignItems: 'center',
      overflowX: 'clip',
      position: 'relative',
      containerType: 'inline-size',
    }}>
      <img src={imgSrc} style={{width: '100%'}}/>
      <Typography style={{
        fontSize: '3.7cqw',
        fontFamily: 'OakesGrotesk',
        fontWeight: 400,
        position: 'absolute',
        right: '5.3cqw',
        top: '3.95cqw',
        color: '#ffffff'
      }}>
        {
          hours && Number(hours) > 0 ? 
          String(hours).padStart(2, '0') + ':' + String(minutes).padStart(2, '0') + ':' + String(seconds).padStart(2, '0') :
          String(minutes).padStart(2, '0') + ':' + String(seconds).padStart(2, '0')
        }
      </Typography>
    </Box>)
  }
})
};


export const countdownRendererImgHalfwidth = (imgSrc: string) => {
  return (({ hours, minutes, seconds, completed }: any) => {

  if (completed) {
    return (
    <Box >
    </Box>);
  } else {
    return (
    <Box style={{
      width: '100%',
      display: 'flex',
      justifyContent: 'flex-start',
      alignItems: 'center',
      overflowX: 'clip',
      position: 'relative',
      containerType: 'inline-size',
    }}>
      <img src={imgSrc} style={{width: '100%'}}/>
      <Typography style={{
        fontSize: '5.4cqw',
        fontFamily: 'OakesGrotesk',
        fontWeight: 400,
        position: 'absolute',
        right: '6.85cqw',
        top: '7.48cqw',
        color: '#ffffff'
      }}>
        {
          hours && Number(hours) > 0 ? 
          String(hours).padStart(2, '0') + ':' + String(minutes).padStart(2, '0') + ':' + String(seconds).padStart(2, '0') :
          String(minutes).padStart(2, '0') + ':' + String(seconds).padStart(2, '0')
        }
      </Typography>
    </Box>)
  }
})
};


// 32-bit string hash (moved from the removed legacy PageOrder). Used to seed
// the deterministic product-suggestion shuffle.
export function getHash(input: string){
  var hash = 0, len = input.length;
  for (var i = 0; i < len; i++) {
    hash  = ((hash << 5) - hash) + input.charCodeAt(i);
    hash |= 0; // to 32bit integer
  }
  return hash;
}
